/**
 * One reconciliation step — Accounts, Metrics or Categories.
 *
 * All three ask the same question: "this value appears in your file; which
 * ExecBridge record is it?" So they share a component rather than three
 * near-identical ones drifting apart.
 *
 * Matching itself is pure and lives in `lib/entity-match.ts`, mirroring the
 * server's QC engine. Resolved rows are pre-filled but still overridable;
 * ambiguous and unresolved rows must be decided or explicitly skipped.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";

import { AlertTriangle, Check, HelpCircle, Plus } from "lucide-react";

import { AddCustomDialog, type MetricChoices } from "./AddCustomDialog";
import { SearchableSelect, type OptionGroup } from "./SearchableSelect";
import Button from "../../Components/Button";
import Pagination from "../../Components/Pagination";
import type { CategoryOption } from "../data/api";
import type { CustomMetricSeed } from "../lib/custom-metric-seed";
import type {
  Candidate,
  DecisionCounts,
  MatchProposal,
} from "../lib/entity-match";
import { effectiveTarget } from "../lib/entity-match";

type Props = {
  title: string;
  hint: string;
  /** Column heading for the source value, e.g. "Account in your file". */
  sourceLabel: string;
  proposals: MatchProposal[];
  candidates: Candidate[];
  decisions: Record<string, string>;
  counts: DecisionCounts;
  onDecide: (source: string, target: string) => void;
  /** Shown when nothing needs reconciling. */
  emptyNote: string;
  /**
   * True for descriptive columns (category): an unmatched value keeps its
   * original text and the row still imports, rather than being excluded.
   */
  keepUnmatched?: boolean;
  /**
   * Enables "Add as custom" on unmatched rows. Absent for Accounts, which are
   * real records created elsewhere — inventing one here would be a data-entry
   * mistake wearing a reconciliation step's clothes.
   */
  customKind?: "metric" | "category";
  /**
   * Categories offered by the metric form, one per real category, by key.
   * Ignored for `customKind="category"`.
   */
  categoryOptions?: CategoryOption[];
  /** What the file says about an unmatched metric, to pre-fill the form. */
  customSeedFor?: (source: string) => CustomMetricSeed | undefined;
  /** Units, formats and scales already in use, offered as suggestions. */
  customChoices?: MetricChoices;
  /** Called after a custom row is created, so the caller can add it to `candidates`. */
  onCustomCreated?: (created: { id: string; name: string }) => void;
  /** Right side of the title row (e.g. file name chip). */
  headerTrailing?: ReactNode;
};

function StatusBadge({
  p,
  target,
  keepUnmatched,
}: {
  p: MatchProposal;
  target: string | null;
  keepUnmatched?: boolean;
}) {
  if (!target && keepUnmatched && p.status === "ambiguous") {
    // Categories: more than one fits — or the file contradicts itself — so
    // nothing was chosen. That needs a decision; it is not a new value.
    // (For accounts and metrics "will skip" below already says what happens.)
    return (
      <span className="inline-flex items-center rounded-md bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-900">
        needs review
      </span>
    );
  }
  if (!target) {
    return keepUnmatched ? (
      <span className="inline-flex items-center rounded-md bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-700">
        will create
      </span>
    ) : (
      <span className="inline-flex items-center rounded-md bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-900">
        will skip
      </span>
    );
  }
  if (p.status === "resolved") {
    return (
      <span className="inline-flex items-center rounded-md bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800">
        <Check className="mr-1 h-3.5 w-3.5" strokeWidth={3} />
        matched
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-md bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800">
      chosen
    </span>
  );
}

export function MatchStep({
  title,
  hint,
  sourceLabel,
  proposals,
  candidates,
  decisions,
  counts,
  onDecide,
  emptyNote,
  keepUnmatched,
  customKind,
  categoryOptions,
  customSeedFor,
  customChoices,
  onCustomCreated,
  headerTrailing,
}: Props) {
  /** The unmatched row a custom row is being created for, if any. */
  const [creatingFor, setCreatingFor] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  /**
   * Which candidates are already claimed by some other row.
   *
   * Two accounts in a file almost never map to the same ExecBridge record, so
   * a target already spoken for is rarely the right answer. Sorting those to
   * the bottom leaves the short, likely list on top — with 47 metric
   * definitions that is the difference between scanning six and scanning all
   * of them.
   *
   * Declared before the empty-state return: a hook after a conditional return
   * changes hook order between renders, which React rejects outright.
   */
  const claimedBy = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of proposals) {
      const t = effectiveTarget(p, decisions);
      if (t) m.set(t, p.source);
    }
    return m;
  }, [proposals, decisions]);

  const pageCount = Math.max(1, Math.ceil(proposals.length / pageSize));
  useEffect(() => {
    setPage(p => Math.min(p, pageCount));
  }, [pageCount]);

  const pagedProposals = useMemo(() => {
    const start = (page - 1) * pageSize;
    return proposals.slice(start, start + pageSize);
  }, [proposals, page, pageSize]);

  if (proposals.length === 0) {
    return (
      <div className="space-y-2">
        <div className="flex flex-wrap items-start gap-3">
          <p className="m-0 min-w-[16rem] flex-1 text-base font-semibold text-gray-900">
            {title}
          </p>
          {headerTrailing}
        </div>
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
          {emptyNote}
        </div>
      </div>
    );
  }

  // Categories only: a value more than one category fits — or whose id and
  // name disagree — is a decision to make, not a new category to create.
  const needsReview = keepUnmatched
    ? proposals.filter(
        p => p.status === "ambiguous" && !effectiveTarget(p, decisions)
      ).length
    : 0;
  const unresolved =
    proposals.filter(p => !effectiveTarget(p, decisions)).length - needsReview;

  const groupsFor = (p: MatchProposal): OptionGroup[] => {
    const mine = effectiveTarget(p, decisions);
    const pending: typeof candidates = [];
    const claimed: typeof candidates = [];
    for (const c of candidates) {
      // A row's own current target stays in the top group; it is not competing
      // with itself.
      const owner = claimedBy.get(c.id);
      if (owner && owner !== p.source && c.id !== mine) claimed.push(c);
      else pending.push(c);
    }
    const toOption = (c: Candidate) => ({
      id: c.id,
      label: c.name,
      keywords: c.id,
    });
    return [
      { heading: "Pending", options: pending.map(toOption) },
      {
        heading: "Already selected",
        options: claimed.map(c => {
          const owner = claimedBy.get(c.id);
          return {
            ...toOption(c),
            // Naming the claimant only helps when it differs from the target.
            // "Jefferies LLC — Jefferies LLC" is noise; the group heading
            // already says the target is taken.
            label: owner && owner !== c.name ? `${c.name} — ${owner}` : c.name,
          };
        }),
      },
    ];
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-[16rem] flex-1">
          <p className="m-0 text-base font-semibold text-gray-900">{title}</p>
          <p className="m-0 mt-0.5 text-sm leading-relaxed text-gray-500">
            {hint}
          </p>
        </div>
        {headerTrailing}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-md bg-gray-100 px-2.5 py-1 text-sm text-gray-700">
          {counts.auto_matched} auto-matched
        </span>
        <span className="rounded-md bg-gray-100 px-2.5 py-1 text-sm text-gray-700">
          {counts.user_matched} user-matched
        </span>
        <span className="rounded-md bg-gray-100 px-2.5 py-1 text-sm text-gray-700">
          {counts.skipped - needsReview}{" "}
          {keepUnmatched ? "will create" : "skipped"}
        </span>
        {needsReview > 0 && (
          <span className="rounded-md bg-amber-100 px-2.5 py-1 text-sm font-medium text-amber-900">
            {needsReview} need{needsReview === 1 ? "s" : ""} review
          </span>
        )}
      </div>

      {needsReview > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {needsReview} value{needsReview === 1 ? "" : "s"} could mean more
            than one category — for example the name says one and the
            file&apos;s Category ID says another. Choose one; the suggestions
            show both.
          </span>
        </div>
      )}

      {unresolved > 0 &&
        (keepUnmatched ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-600">
            {unresolved} value{unresolved === 1 ? "" : "s"} have no counterpart
            in the rubric and will be created on import, keeping the original
            text. Their rows still import; QC records a warning. Map one only if
            you want an existing rubric name stored instead.
          </div>
        ) : (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {unresolved} value{unresolved === 1 ? "" : "s"} will be skipped.
              Rows carrying them will not reach production — choose a target, or
              continue if skipping is what you want.
            </span>
          </div>
        ))}

      {/* Full table in page flow — page scrolls (no nested max-height). */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full border-collapse text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                {sourceLabel}
              </th>
              <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                Rows
              </th>
              <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                Status
              </th>
              {/* Suggestion is its own fact, not a suffix on the chosen value —
                  "Acme Corp — suggested" read as part of the account name. */}
              <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                Suggested
              </th>
              <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                Maps to
              </th>
            </tr>
          </thead>
          <tbody>
            {pagedProposals.map(p => {
              const target = effectiveTarget(p, decisions);
              return (
                <tr key={p.source} className="border-t border-gray-100">
                  <td className="max-w-[240px] truncate px-3 py-2.5 font-semibold text-gray-900">
                    {p.source}
                  </td>
                  <td className="px-3 py-2.5 text-left tabular-nums text-gray-700">
                    {p.rowCount.toLocaleString()}
                  </td>
                  <td className="px-3 py-2.5">
                    <StatusBadge
                      p={p}
                      target={target}
                      keepUnmatched={keepUnmatched}
                    />
                  </td>
                  <td className="max-w-[220px] px-3 py-2.5">
                    {p.detail && p.detail.length > 0 ? (
                      // A conflict: say what each side says instead of
                      // hiding the second option behind "+1".
                      <div className="space-y-0.5 text-xs leading-snug text-amber-900">
                        {p.detail.map(line => (
                          <div key={line}>{line}</div>
                        ))}
                      </div>
                    ) : p.suggestions.length === 0 ? (
                      <span className="text-gray-400">—</span>
                    ) : (
                      <div title={p.suggestions.map(s => s.name).join(", ")}>
                        <span className="block truncate text-gray-600">
                          {p.suggestions[0].name}
                        </span>
                        {p.suggestions.length > 1 &&
                          p.status !== "resolved" && (
                            // Name the other close matches rather than "+N";
                            // only where a person still has to choose.
                            <span className="block truncate text-xs text-gray-400">
                              Also close:{" "}
                              {p.suggestions
                                .slice(1)
                                .map(s => s.name)
                                .join(", ")}
                            </span>
                          )}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <SearchableSelect
                        ariaLabel={`Maps to, for ${p.source}`}
                        value={decisions[p.source] ?? p.chosenId ?? "skip"}
                        onChange={v => onDecide(p.source, v)}
                        leadingOption={{
                          id: "skip",
                          // A conflict is a decision, not a new value: no
                          // "Create on import" default for it.
                          label:
                            keepUnmatched && p.status === "ambiguous"
                              ? "— Choose one —"
                              : keepUnmatched
                                ? "— Create on import —"
                                : "— Skip these rows —",
                        }}
                        groups={groupsFor(p)}
                        searchPlaceholder={`Search ${sourceLabel
                          .replace(/ in your file$/i, "")
                          .toLowerCase()}s…`}
                        className="w-[260px]"
                      />
                      {p.status === "ambiguous" && (
                        <span
                          title="More than one record fits this value, so it was not auto-matched."
                          className="text-gray-400"
                        >
                          <HelpCircle className="h-4 w-4" />
                        </span>
                      )}
                      {/* Only offered where there is nothing to map onto.
                          Beside a matched row it would invite a duplicate of a
                          definition that already exists. */}
                      {customKind && !target && (
                        <Button
                          variant="outline-primary"
                          className="h-8 shrink-0 px-3 text-xs"
                          onClick={() => setCreatingFor(p.source)}
                          icon={<Plus className="h-3.5 w-3.5" />}
                        >
                          Add as custom
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        mode="static"
        currentPage={page}
        pageSize={pageSize}
        dataLength={proposals.length}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        pageSizeOptions={[15, 25, 50, 100]}
        className="mt-1"
      />

      {customKind && creatingFor !== null && (
        <AddCustomDialog
          kind={customKind}
          initialName={creatingFor}
          categories={categoryOptions ?? []}
          seed={customSeedFor?.(creatingFor)}
          choices={customChoices}
          open
          onOpenChange={open => {
            if (!open) setCreatingFor(null);
          }}
          onCreated={created => {
            onCustomCreated?.(created);
            // Map the row that prompted this straight onto what was just
            // created; that is the only reason the user opened the dialog.
            onDecide(creatingFor, created.id);
            setCreatingFor(null);
          }}
        />
      )}
    </div>
  );
}

export default MatchStep;
